import { CircleQuestionMark } from "lucide-react";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { Tag } from "../Tag";
import { TagSize } from "../Tag/types";
import { Tooltip } from "../Tooltip";
import { Text } from "../Typography";
import type { FormFieldProps } from "./types";

export const FormField = ({
  children,
  label,
  description,
  message,
  required = true,
  className,
  accessory,
  tooltipContent,
  htmlFor,
  size = "md",
}: FormFieldProps) => {
  const { t } = useTranslation();

  const rootClass = cn("w-full flex flex-col gap-2", className);
  const textClass = cn(size === "lg" ? "text-md" : "text-sm");
  const tagSize: TagSize = size === "lg" ? "md" : "sm";

  const tooltipJSX = tooltipContent && (
    <span className="inline-block h-[18px] leading-[18px] align-sub ms-1">
      <Tooltip content={tooltipContent}>
        <CircleQuestionMark
          size={18}
          className="text-neutral-500/80 focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary-500 focus-visible:outline-offset-0 rounded-full"
          tabIndex={0}
        />
      </Tooltip>
    </span>
  );

  return (
    <div className={rootClass}>
      {label && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            {htmlFor ? (
              <label
                htmlFor={htmlFor}
                className={cn("text-neutral-500", textClass)}
              >
                {label}
                {tooltipJSX}
              </label>
            ) : (
              <span className={cn("text-neutral-500", textClass)}>
                {label}
                {tooltipJSX}
              </span>
            )}
            {!required && (
              <Tag
                color="neutral"
                size={tagSize}
                label={t("formField.optional")}
              />
            )}
            {accessory && <div className="ms-auto">{accessory}</div>}
          </div>
          {description && (
            <Text className={cn(textClass, "text-neutral-500/50")}>
              {description}
            </Text>
          )}
        </div>
      )}
      {children}
      {message && (
        <Text size="sm" className="text-danger-500">
          {message}
        </Text>
      )}
    </div>
  );
};
